/*!
 * SPDX-License-Identifier: AGPL-3.0-only
 * 
 * PV Tool — AGPL Community Edition
 * Based on the last AGPL-3.0 version published on 2026-03-18
 * 
 * Copyright (c) 2026 DanteAlighieri13210914
 * Copyright (c) 2026 Contributors to PV Tool AGPL Community Edition
 * 
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, version 3 of the License.
 * 
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 * 
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 * 
 * Source repository: https://github.com/yandujun363/pv-tool-agpl
 */

import type { LocaleKey } from './zh';

export const ja: Record<LocaleKey, string> = {
  // Page
  'page_title': 'PV Tool AGPL Community Edition - PVビジュアルジェネレーター',
  'initializing': 'PV Tool AGPL Community Edition を初期化中...',
  'initializing_desc': 'しばらくお待ちください。初回読み込み時にモニターのリフレッシュレートを検出します',

  // Panel titles
  'postfx': 'ポスト Post FX',
  'effects_library': 'エフェクト Effects',

  // Left panel
  'template': 'テンプレート Template',
  'custom': '✦ カスタム Custom',
  'canvas_color': 'キャンバス色 Canvas',
  'follow_template': 'テンプレに従う',
  'text_label': 'テキスト Text（/で分割）',
  'expand': '展開',
  'collapse': '折る',
  'seg_duration': 'セグメント間隔',
  'anim_speed': 'アニメ速度',
  'motion_intensity': 'モーション強度',
  'bg_opacity': '背景不透明度',
  'media': 'メディア Media',
  'media_mode': 'メディアモード Mode',
  'auto_fit': '自動フィット Auto Fit',
  'free_mode': 'フリー Free',
  'apply': '適用 Apply',
  'audio': 'オーディオ Audio',
  'pause': '⏸ 一時停止',
  'play': '▶ 再生',
  'playing': '♪ 再生中',
  'paused': '⏹ 停止中',
  'bpm': 'BPM',
  'beat_react': 'ビート反応 Beat React',

  // Right panel — Post FX
  'shake': 'シェイク Shake',
  'zoom': 'ズーム Zoom',
  'tilt': 'ティルト Tilt',
  'glitch': 'グリッチ Glitch',
  'hue_shift': '色相シフト Hue',
  'media_position': '画像位置 Media Position',
  'offset_x': 'Xオフセット',
  'offset_y': 'Yオフセット',
  'scale': 'スケール',
  'rec': '録画 REC',
  'stop': '停止 STOP',
  'packing': 'パッキング中...',
  'alpha_export': '透明背景出力 Alpha',
  'srt_import': '字幕タイムライン SRT',
  'lrc_import': 'LRC読込',
  'timer_label': 'タイマー Time',

  'render': 'レンダリング設定',
  'render_resolution': '解像度',
  'render_resolution_auto': '自動 (パフォーマンス優先)',
  'render_resolution_1x': '1x (標準)',
  'render_resolution_1_5x': '1.5x (高)',
  'render_resolution_2x': '2x (超高)',
  'render_resolution_custom': 'カスタム WxH',
  'render_resolution_width': '幅',
  'render_resolution_height': '高さ',
  'render_resolution_apply': '適用',
  'render_fps': 'フレームレート制限',
  'render_fps_auto': '自動 (最大)',
  'render_fps_30': '30 FPS',
  'render_fps_60': '60 FPS',
  'render_fps_custom': 'カスタム',
  'render_fps_value': 'FPS',
  'render_fps_current': '現在のFPS: {fps}',
  'fps_warning_exceed_monitor': '設定されたFPS({fps})がモニターのリフレッシュレート({max})を超えています',
  'render_scale_mode': '画面スケールモード',
  'scale_mode_stretch': '引き伸ばし (フルスクリーン)',
  'scale_mode_contain': 'アスペクト比維持 (黒帯)',

  // Right panel — Export
  'export': '書き出し Export',
  'copy_url': 'URL をコピー',

  // Right panel — Listen
  'listen': 'モニタリング Listen',
  'listen_now_playing': 'Now Playing をモニタリング',

  // Color swatch titles
  'white': '白',
  'black': '黒',
  'blue': '青',
  'red': '赤',
  'yellow': '黄',
  'pink': 'ピンク',
  'p5red': 'P5赤',

  // Effect categories
  'cat_background': '背景',
  'cat_geometry': 'ジオメトリ装飾',
  'cat_lines': 'ライン構造',
  'cat_text': 'テキスト',
  'cat_overlay': 'オーバーレイ',
  'cat_hud': 'HUD',
  'cat_organic': 'オーガニック',
  'cat_grunge': 'デジタルグランジ',
  'cat_special': '特殊シェイプ',

  // Template names
  'tpl_p5': 'P5',
  'tpl_blueBold': 'ブルーインパクト',
  'tpl_kineticSplit': '斬撃',
  'tpl_bluePlane': '青い構成（動画推奨）',
  'tpl_cyberGrunge': 'サイバー廃墟',
  'tpl_geometric': 'ジオメトリック',
  'tpl_rainCity': 'マトリックス',
  'tpl_cyberpunkHud': 'ナイトシティ監視（動画推奨）',
  'tpl_emotionCinema': 'エモーションシネマ（動画推奨）',
  'tpl_hystericNight': 'ヒステリックナイト',
  'tpl_spiderWeb': 'スパイダーウェブ',
  'tpl_staggeredText': 'スタッガードテキスト',
  'tpl_calmVillain': 'クールヴィラン',
  'tpl_girlyClouds': 'ピンククラウド',
  'tpl_sweetPink': 'グリッドスカラップ',
  'tpl_flyMeToTheMoon': 'Fly Me to the Moon',
  'tpl_kawaiPixel': 'Kawaiiピクセル',

  // File picker
  'choose_file': 'ファイル選択',
  'no_file': '未選択',

  //Export and import project
  'project_file': 'プロジェクトファイル',
  'export_project': 'プロジェクトをエクスポート',
  'import_project': 'プロジェクトをインポート',
  'export_success': 'エクスポート成功',
  'import_success': 'インポート成功',
  'import_failed': 'インポート失敗',
  'project_file_desc': 'PV Tool CE プロジェクトファイル (.pvtoolce)',
  'confirm_import': 'インポートすると現在の設定が置き換えられます。続行しますか？',
  'export_with_resources': 'リソースファイルを含める',
  'export_with_media': '背景メディアを含める',
  'export_with_audio': 'オーディオを含める',
  'export_with_lyrics': '歌詞を含める',
  'lrc_imported': '歌詞をインポートしました',

  // Sharecode & template management
  'export_code': 'シェアコード出力',
  'export_json_confirm': 'JSON形式でエクスポート（可読）？\nOK=JSON、キャンセル=従来のバイナリ',
  'import_code': 'シェアコード入力',
  'import_btn': '読込',
  'code_copied': 'シェアコードをクリップボードにコピーしました',
  'code_invalid': 'コード無効',
  'save_tpl': '保存',
  'delete_tpl': '削除',
  'confirm': '確認',
  'cancel': 'キャンセル',
  'tpl_name_placeholder': 'テンプレート名を入力...',
  'confirm_delete': '削除確認',
  'share_code': 'シェアコード Share Code',
  'copy': 'コピー',
  'paste_code': 'シェアコードを貼り付け...',


  // Effect categories
  'ecat_背景': '背景',
  'ecat_几何装饰': 'ジオメトリ装飾',
  'ecat_线条结构': 'ライン構造',
  'ecat_文字': 'テキスト',
  'ecat_叠加效果': 'オーバーレイ',
  'ecat_HUD': 'HUD',
  'ecat_有机形态': 'オーガニック',
  'ecat_数字废墟': 'デジタルグランジ',
  'ecat_特殊形状': '特殊シェイプ',
  'ecat_云朵条纹': 'クラウド＆ストライプ',
  'ecat_Kawaii像素': 'Kawaiiピクセル',

  // Effect labels
  'fx_textureBackground': 'テクスチャ背景 Texture BG',
  'fx_gradientOverlay': 'グラデーション Gradient',
  'fx_triangleGrid': '三角グリッド TriGrid',
  'fx_backgroundBlocks': '背景ブロック Blocks',
  'fx_breathingBlocks': 'ブリージングブロック Blocks',
  'fx_checkerboard': 'チェッカーボード Checker',
  'fx_concentricCircles': '同心円 Circles',
  'fx_diamondShapes': 'ダイヤモンド Diamonds',
  'fx_crossPattern': '十字パターン Cross',
  'fx_scatteredShapes': '散布シェイプ Shapes',
  'fx_halftoneBlocks': 'ハーフトーン Halftone',
  'fx_shadowShapes': 'シャドウシェイプ Shadow',
  'fx_centeredSquares': 'センタースクエア Squares',
  'fx_balancingCircles': 'バランス円 Circles',
  'fx_radialRectangles': '放射矩形 RadialRect',
  'fx_starTrail': '星軌 StarTrail',
  'fx_planet': '惑星 Planet',
  'fx_flowingLines': 'フローライン Lines',
  'fx_diagonalFill': '斜線フィル Diagonal',
  'fx_diagonalHatch': '斜線ハッチ Hatch',
  'fx_parallelogramStripes': '平行四辺形 Stripes',
  'fx_diagonalSplit': '対角分割 Split',
  'fx_diagonalStructure': '対角構造 DiagStruct',
  'fx_burstLines': '放射線 Burst',
  'fx_screenBorder': 'スクリーンボーダー Border',
  'fx_dashedGuideLines': 'ダッシュライン Dashed',
  'fx_perspectiveGrid': 'パースグリッド PerspGrid',
  'fx_compositionGuides': '構成ガイド CompGuide',
  'fx_webLines': 'ウェブライン WebLines',
  'fx_heroText': 'メインタイトル Hero',
  'fx_scatteredText': '散布テキスト Scattered',
  'fx_textStrip': 'テキストストリップ Strip',
  'fx_textCards': 'テキストカード Cards',
  'fx_bigOutlineText': '大文字アウトライン BigText',
  'fx_cuteOutlineText': 'キュートアウトライン CuteText',
  'fx_layeredText': 'レイヤードテキスト Layered',
  'fx_glowTextCards': 'グローカード GlowCards',
  'fx_verticalSubText': '縦書きサブ VertSub',
  'fx_formulaText': '数式テキスト Formula',
  'fx_fallingText': 'テキストレイン Falling',
  'fx_staggeredText': 'スタッガードテキスト Staggered',
  'fx_colorMask': 'カラーマスク Mask',
  'fx_chromaticAberration': '色収差 Chromatic',
  'fx_glitchBars': 'グリッチバー Glitch',
  'fx_vignette': 'ビネット Vignette',
  'fx_scanlines': 'スキャンライン Scanlines',
  'fx_filmGrain': 'フィルムグレイン Grain',
  'fx_dotScreen': 'ドットスクリーン Dots',
  'fx_hudCorners': 'HUDコーナー Corners',
  'fx_hudStatusText': 'HUDステータス Status',
  'fx_hudInfoPanel': 'HUD情報パネル Panel',
  'fx_rulerGuide': '定規ガイド Ruler',
  'fx_targetGuide': 'ターゲットライン Target',
  'fx_motionBrackets': 'モーションブラケット Motion',
  'fx_glowRing': 'グローリング GlowRing',
  'fx_lightSpot': 'ライトスポット LightSpot',
  'fx_organicBlob_blob': 'オーガニックBlob Blob',
  'fx_organicBlob_wave': 'ウェーブ Wave',
  'fx_organicBlob_cloud': 'クラウド Cloud',
  'fx_smearBrush': 'スミアブラシ Smear',
  'fx_noiseText': 'ノイズテキスト NoiseText',
  'fx_dataMonitors': 'データモニター Monitors',
  'fx_paperTear': 'ペーパーテア PaperTear',
  'fx_jigsawGrid': 'ジグソーグリッド Jigsaw',
  'fx_edgeClouds': 'エッジクラウド EdgeClouds',
  'fx_pinkStripes': 'ピンクストライプ PinkStripes',
  'fx_pinkGrid': 'ピンクグリッド PinkGrid',
  'fx_scalloppedBorder': 'スカラップボーダー Scallop',
  'fx_pulsingCircle': 'パルスサークル Pulse',
  'fx_pixelWindow': 'ピクセルウィンドウ PixelWin',
  'fx_desktopIcon': 'デスクトップアイコン Icon',
  'fx_pixelBackground': 'ピクセル背景 PixelBG',
  'fx_pixelTypewriter': 'ピクセルタイプライター PixelType',

  // Hint messages
  'hint_press': '押す',
  'hint_hide_panels': 'でパネルを非表示にする',

  // Modal messages
  'modal_confirm': 'OK',
  'np_fail_title': '再生に失敗しました：Now Playingサービスが検出されません！',
  'np_fail_body': 'Now Playingは20以上の音楽アプリに対応した無料のオープンソース楽曲表示ツールです。以下からダウンロードしてください：',
  'copy_url_settings': '設定',
  'copy_url_transparent_bg': '透明背景',
  'copy_url_use_template': '現在のテンプレートを使用',
  'copy_url_copy_btn': 'コピー',
  'url_copied': 'URLをクリップボードにコピーしました',
  'listen_np_tip': '',
  'copy_url_tip': '',

  // WesingCap
  'listen_wesingcap': 'Nexus WesingCap を監視',
  'listen_wesingcap_tip': '',
  'nwc_fail_title': '接続失敗：Nexus WesingCapサービスが検出されません！',
  'nwc_fail_body': 'Metabox Nexus WesingCapは全民K歌デスクトップアプリから歌詞を取得するツールです。詳細はこちら：',
  'nwc_settings_title': 'Nexus WesingCap 設定',
  'nwc_ws_addr': 'WebSocket アドレス',
  'nwc_ws_addr_placeholder': 'デフォルト localhost:8765',
  'nwc_save': '保存',
  'nwc_saved': '保存しました',
  'nwc_disconnected': 'WesingCap 接続が切断されました',
  
  // Copyright
  'license_title': 'AGPL-3.0 オープンソースライセンス',
  'license_text': 'このツールは GNU Affero General Public License v3.0 の下でオープンソース化されています。',
  'license_source': 'ソースコード',
  'license_copyright': '© 2026 PV Tool — AGPL Community Edition',
};
